/* Every Claude Code tool that a plan run can call, and how Pieve decides each call (passthrough ADR-005,
   point 2). Checked against Claude Code 2.1.312 and the tool schemas of @anthropic-ai/claude-agent-sdk
   0.3.312. A tool without an entry is denied, so a new Claude Code tool waits for an entry here.

   - `pieveTool`: the permission keys of that Pieve tool decide the call. `pathField` and `commandField` name the
     input fields that the approval card shows.
   - `allow`: no permission key. Every tool call inside a subagent gets its own check.
   - `pieveServerOnly`: allowed only for Pieve's own tool server.
   - `question`: a question card answered through the run's Mac.
   - `deny`: always denied, and also passed to Claude Code as a disallowed tool. */
const ALWAYS_DENIED_MESSAGE = 'Pieve does not allow this tool in a Pieve thread.';

export const PIEVE_MCP_SERVER_NAME = 'pieve';
// Claude Code names a tool server's tools `mcp__<server>__<tool>`.
export const PIEVE_MCP_TOOL_PREFIX = `mcp__${PIEVE_MCP_SERVER_NAME}__`;
// Claude Code's tool for a question to the person, answered through a card on the run's Mac.
export const CLAUDE_CODE_QUESTION_TOOL = 'AskUserQuestion';

const denied = (names) => names.map((name) => [name, { deny: ALWAYS_DENIED_MESSAGE }]);
const allowed = (names) => names.map((name) => [name, { allow: true }]);
const pieveServerOnly = (names) => names.map((name) => [name, { pieveServerOnly: true }]);

export const CLAUDE_CODE_TOOLS = Object.freeze(
  Object.fromEntries([
    ['Read', { pieveTool: 'files_read', pathField: 'file_path' }],
    ['Glob', { pieveTool: 'files_read', pathField: 'path' }],
    ['Grep', { pieveTool: 'files_read', pathField: 'path' }],
    ['Edit', { pieveTool: 'files_write', pathField: 'file_path' }],
    ['Write', { pieveTool: 'files_write', pathField: 'file_path' }],
    ['NotebookEdit', { pieveTool: 'files_write', pathField: 'notebook_path' }],
    ['Bash', { pieveTool: 'exec_command', commandField: 'command' }],
    ['WebFetch', { pieveTool: 'read_web_page' }],
    ['WebSearch', { pieveTool: 'web_search' }],
    // Subagents (L22), the session's own to-do list, plan mode and tool loading.
    ...allowed(['Agent', 'Task', 'TodoWrite', 'TaskCreate', 'TaskGet', 'TaskUpdate', 'TaskList', 'EnterPlanMode', 'ExitPlanMode', 'ToolSearch', 'RefreshMcpTools']),
    ...pieveServerOnly(['ListMcpResourcesTool', 'ReadMcpResourceTool', 'ReadMcpResourceDirTool', 'ListMcpResources', 'ReadMcpResource', 'ReadMcpResourceDir']),
    [CLAUDE_CODE_QUESTION_TOOL, { question: true }],
    // Work outside a Pieve run, reach outside the thread, or the person's own setup.
    ...denied(['CronCreate', 'CronDelete', 'CronList', 'ScheduleWakeup', 'Monitor', 'RemoteTrigger', 'SendMessage', 'PushNotification', 'EnterWorktree', 'ExitWorktree', 'TaskStop', 'Skill', 'SlashCommand', 'Workflow', 'ProposeSkills', 'ProposeGoal', 'Artifact', 'ClaudeDesign', 'Projects', 'SendFeedback', 'ReportFindings', 'ReadNotifications', 'OfferChromeSetup', 'ShowOnboardingRolePicker'])
  ])
);

// Passed to Claude Code as disallowed tools, so the model never sees them.
export const CLAUDE_CODE_DISALLOWED_TOOLS = Object.freeze(Object.keys(CLAUDE_CODE_TOOLS).filter((name) => Boolean(CLAUDE_CODE_TOOLS[name].deny)));
